import { circleLoop, ProfileLoop, ProfilePoint } from './profile';

/** Type 2 cross-section knobs, in metres, rail line at y = 0. */
export interface Type2ShapeParams {
  topGap: number;
  bottomWidth: number;
  vHeight: number;
  /** Width at the top of the rack-top section; widen it to reach the V arms. */
  rackTopWidth: number;
  rackTopHeight: number;
  rackBottomHeight: number;
  beamHeight: number;
  circleDiameter: number;
  thickness: number;
}

export interface Type2Layout {
  leftTopX: number;
  rightTopX: number;
  leftX: number;
  rightX: number;
  /** Top corners of the rack-top section, where the V arms land. */
  rackTopLeftX: number;
  rackTopRightX: number;
  topY: number;
  vBottomY: number;
  rackTopBottomY: number;
  rackBottomY: number;
  beamBottomY: number;
  r: number;
  thickness: number;
}

/** Sharp corners: miter until the spike gets silly, matching the SVG default. */
const MITER_LIMIT = 4;

export function type2Layout(p: Type2ShapeParams): Type2Layout {
  const thickness = Math.max(p.thickness, 1e-4);
  const top = Math.max(p.topGap, 1e-4);
  // Each stem side carries a bar of its own, so they can never overlap.
  const bottom = Math.max(p.bottomWidth, thickness);
  const rackTop = Math.max(p.rackTopWidth, thickness);
  const r = Math.max(p.circleDiameter / 2, 1e-4);
  const topY = 0;
  const vBottomY = topY - Math.max(p.vHeight, 0);
  const rackTopBottomY = vBottomY - Math.max(p.rackTopHeight, 0);
  const rackBottomY = rackTopBottomY - Math.max(p.rackBottomHeight, 0);
  const beamBottomY = rackBottomY - Math.max(p.beamHeight, 0);
  return {
    leftTopX: -top / 2,
    rightTopX: top / 2,
    leftX: -bottom / 2,
    rightX: bottom / 2,
    rackTopLeftX: -rackTop / 2,
    rackTopRightX: rackTop / 2,
    topY,
    vBottomY,
    rackTopBottomY,
    rackBottomY,
    beamBottomY,
    r,
    thickness,
  };
}

function loopArea(points: ProfilePoint[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    area += p.x * q.y - q.x * p.y;
  }
  return area / 2;
}

/** Skip slivers; reverse clockwise rings so sweep caps face outward. */
function pushConvexFill(fills: ProfilePoint[][], points: ProfilePoint[]): void {
  const area = loopArea(points);
  if (Math.abs(area) < 1e-12) return;
  fills.push(area > 0 ? points : points.slice().reverse());
}

/**
 * Type 2 as the cross-section viewer draws it: a wireframe of bar centre-lines
 * stroked at {@link Type2Layout.thickness}, convex solid fills, plus two solid
 * rail rods. The mesh builder and the SVG viewer both consume this, so the 2D
 * preview and the 3D track can never drift apart.
 */
export interface Type2Skeleton {
  /** Bar centre-lines (open polylines), stroked at `thickness`. */
  bars: ProfilePoint[][];
  /**
   * Convex filled regions (CCW, closed implicitly). Beam slab, plus the left
   * and right side webs from each rail down to the rack (rack centre stays open).
   */
  fills: ProfilePoint[][];
  /** Solid rail rods. */
  rails: { x: number; y: number; r: number }[];
  thickness: number;
}

export function type2Skeleton(p: Type2ShapeParams): Type2Skeleton {
  const L = type2Layout(p);
  const bars: ProfilePoint[][] = [
    // Outer wireframe: rail centre → top of the rack-top section → in to the
    // stem → down → across the beam base → back up the far side → far rail
    // centre. The rack-top section is a trapezoid when its top is wider.
    [
      { x: L.leftTopX, y: L.topY },
      { x: L.rackTopLeftX, y: L.vBottomY },
      { x: L.leftX, y: L.rackTopBottomY },
      { x: L.leftX, y: L.beamBottomY },
      { x: L.rightX, y: L.beamBottomY },
      { x: L.rightX, y: L.rackTopBottomY },
      { x: L.rackTopRightX, y: L.vBottomY },
      { x: L.rightTopX, y: L.topY },
    ],
  ];
  // Inner V walls only exist once the rack-top section gives them somewhere to
  // land; at zero height they would sit exactly on the outer V arms.
  if (p.rackTopHeight > 1e-6) {
    bars.push(
      [
        { x: L.leftTopX, y: L.topY },
        { x: L.leftX, y: L.rackTopBottomY },
      ],
      [
        { x: L.rightTopX, y: L.topY },
        { x: L.rightX, y: L.rackTopBottomY },
      ],
    );
  }
  // Rack dividers. A zero-height section collapses the two onto each other, and
  // a zero-height beam collapses the lower one onto the outer wireframe bottom —
  // drop the duplicates rather than sweeping coincident bars.
  const dividerYs: number[] = [];
  for (const y of [L.rackTopBottomY, L.rackBottomY]) {
    if (Math.abs(y - L.beamBottomY) < 1e-9) continue;
    if (dividerYs.some((prev) => Math.abs(prev - y) < 1e-9)) continue;
    dividerYs.push(y);
  }
  for (const y of dividerYs) {
    bars.push([
      { x: L.leftX, y },
      { x: L.rightX, y },
    ]);
  }
  // Convex slabs. Side webs sit between the outer V and the inner wall so the
  // rack cavity stays open; a full-width V/rack fill would close the teeth bay.
  const fills: ProfilePoint[][] = [];
  pushConvexFill(fills, [
    { x: L.leftTopX, y: L.topY },
    { x: L.rackTopLeftX, y: L.vBottomY },
    { x: L.leftX, y: L.rackTopBottomY },
  ]);
  pushConvexFill(fills, [
    { x: L.rightTopX, y: L.topY },
    { x: L.rightX, y: L.rackTopBottomY },
    { x: L.rackTopRightX, y: L.vBottomY },
  ]);
  if (p.beamHeight > 1e-6) {
    pushConvexFill(fills, [
      { x: L.leftX, y: L.beamBottomY },
      { x: L.rightX, y: L.beamBottomY },
      { x: L.rightX, y: L.rackBottomY },
      { x: L.leftX, y: L.rackBottomY },
    ]);
  }
  return {
    bars,
    fills,
    rails: [
      { x: L.leftTopX, y: L.topY, r: L.r },
      { x: L.rightTopX, y: L.topY, r: L.r },
    ],
    thickness: L.thickness,
  };
}

/** Rectangle centred on a segment, CCW. */
export function orientedBoxLoop(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  width: number,
): ProfileLoop {
  const loops = strokePolylineLoops(
    [
      { x: x1, y: y1 },
      { x: x2, y: y2 },
    ],
    width,
  );
  return loops[0];
}

/** Unit normal to the left of travel from `a` to `b`. */
function leftNormal(a: ProfilePoint, b: ProfilePoint): ProfilePoint {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1e-9;
  return { x: -dy / len, y: dx / len };
}

/**
 * Per-vertex offset vectors for a stroked open polyline: segment normals at the
 * ends (butt caps) and mitred bisectors at the joints, so corners come to a
 * point instead of being rounded off.
 */
function miterVectors(points: ProfilePoint[]): ProfilePoint[] {
  const segs: ProfilePoint[] = [];
  for (let i = 0; i < points.length - 1; i++) segs.push(leftNormal(points[i], points[i + 1]));
  const out: ProfilePoint[] = [];
  for (let i = 0; i < points.length; i++) {
    if (i === 0) {
      out.push(segs[0]);
    } else if (i === points.length - 1) {
      out.push(segs[segs.length - 1]);
    } else {
      const n1 = segs[i - 1];
      const n2 = segs[i];
      const denom = 1 + n1.x * n2.x + n1.y * n2.y;
      if (denom < 1e-6) {
        out.push(n2);
        continue;
      }
      let m = { x: (n1.x + n2.x) / denom, y: (n1.y + n2.y) / denom };
      const len = Math.hypot(m.x, m.y);
      if (len > MITER_LIMIT) m = { x: (m.x / len) * MITER_LIMIT, y: (m.y / len) * MITER_LIMIT };
      out.push(m);
    }
  }
  return out;
}

/**
 * Stroke an open polyline as one convex quad per segment, mitred at the joints
 * and butt-capped at the ends. Convex on purpose: `sweepProfile` fan-caps its
 * profiles, so a single concave outline would cap wrong. Adjacent quads share
 * their mitre edge exactly, so the union is the sharp-cornered stroke outline.
 */
export function strokePolylineLoops(points: ProfilePoint[], width: number): ProfileLoop[] {
  const pts: ProfilePoint[] = [];
  for (const p of points) {
    const last = pts[pts.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1e-9) pts.push(p);
  }
  // A collinear vertex mitres to the plain segment normal, so its two quads
  // tile the one they replace — drop it and keep the loop set minimal.
  for (let i = pts.length - 2; i > 0; i--) {
    const a = pts[i - 1];
    const b = pts[i];
    const c = pts[i + 1];
    const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    // Positive dot keeps a 180° doubling-back, which is collinear but not straight.
    const dot = (b.x - a.x) * (c.x - b.x) + (b.y - a.y) * (c.y - b.y);
    const scale = Math.hypot(b.x - a.x, b.y - a.y) * Math.hypot(c.x - b.x, c.y - b.y);
    if (scale > 0 && dot > 0 && Math.abs(cross) / scale < 1e-9) pts.splice(i, 1);
  }
  if (pts.length < 2) return [];
  const hw = Math.max(width, 1e-4) / 2;
  const m = miterVectors(pts);
  const loops: ProfileLoop[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const ma = m[i];
    const mb = m[i + 1];
    loops.push({
      points: [
        { x: a.x - ma.x * hw, y: a.y - ma.y * hw },
        { x: b.x - mb.x * hw, y: b.y - mb.y * hw },
        { x: b.x + mb.x * hw, y: b.y + mb.y * hw },
        { x: a.x + ma.x * hw, y: a.y + ma.y * hw },
      ],
    });
  }
  return loops;
}

/** Type 2 sweep profiles: solid rail rods, convex filled slabs (beam + left/right
 * side webs), and the remaining stroked wireframe.
 */
export function type2TrackProfiles(
  p: Type2ShapeParams,
  railSegments = 10,
): { rails: ProfileLoop[]; spine: ProfileLoop[] } {
  const sk = type2Skeleton(p);
  return {
    rails: sk.rails.map((c) => circleLoop(c.x, c.y, c.r, railSegments)),
    spine: [
      ...sk.fills.map((points) => ({ points })),
      ...sk.bars.flatMap((bar) => strokePolylineLoops(bar, sk.thickness)),
    ],
  };
}
